import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import AuthGuard from '@/components/feature/AuthGuard';
import Navbar from '@/components/feature/Navbar';
import { supabase } from '@/lib/supabase';

interface TeamInfo {
  id: string;
  registration_id: string;
  team_name: string;
  status: string;
  selected_problem_statement_id?: string;
  college_name?: string;
}

export default function ParticipantDashboard() {
  const { profile, signOut } = useAuth();
  const [team, setTeam] = useState<TeamInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [paymentPaid, setPaymentPaid] = useState(false);
  const [announcements, setAnnouncements] = useState<{ id: string; title: string; content: string; published_at: string }[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch team info
        const { data: tData } = await supabase
          .from('teams')
          .select('id, registration_id, team_name, status, selected_problem_statement_id, college_name')
          .eq('leader_id', profile?.id || '')
          .maybeSingle();
        if (tData) setTeam(tData);

        // Check payment
        if (tData) {
          const { data: pData } = await supabase
            .from('payments')
            .select('status')
            .eq('team_id', tData.id)
            .maybeSingle();
          if (pData?.status === 'paid') setPaymentPaid(true);
        }

        // Fetch announcements
        const { data: aData } = await supabase
          .from('announcements')
          .select('id, title, content, published_at')
          .order('published_at', { ascending: false })
          .limit(5);
        if (aData) setAnnouncements(aData || []);
      } catch {
        // silently handle
      } finally {
        setLoading(false);
      }
    };
    if (profile?.id) fetchData();
  }, [profile]);

  return (
    <AuthGuard requiredRole="participant">
      <>
        <Navbar />
        <main className="min-h-screen pt-20 pb-16 bg-background-50">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="font-heading text-2xl font-bold text-foreground-950">Participant Dashboard</h1>
                <p className="text-sm text-foreground-500 mt-1">Welcome back, {profile?.full_name || 'Team Leader'}</p>
                {team && <p className="text-xs text-foreground-400 mt-0.5">Team: {team.team_name} ({team.registration_id})</p>}
              </div>
              <button
                onClick={signOut}
                className="px-4 py-2 rounded-lg border border-background-300 text-foreground-600 text-sm hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Sign Out
              </button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 rounded-full border-2 border-primary-500 border-t-transparent animate-spin"></div>
              </div>
            ) : (
              <>
                {/* Status Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mb-8">
                  <div className="p-5 rounded-xl border border-background-200/70 bg-background-50">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center mb-3">
                      <i className="ri-team-line text-lg text-primary-600"></i>
                    </div>
                    <p className="text-xs text-foreground-500">Registration Status</p>
                    <p className="text-lg font-bold text-foreground-900 mt-0.5">
                      <span className="inline-flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-accent-500"></span>
                        Registered
                      </span>
                    </p>
                  </div>
                  <div className="p-5 rounded-xl border border-background-200/70 bg-background-50">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${paymentPaid ? 'bg-accent-100' : 'bg-amber-100'}`}>
                      <i className={`text-lg ${paymentPaid ? 'ri-check-double-line text-accent-600' : 'ri-time-line text-amber-600'}`}></i>
                    </div>
                    <p className="text-xs text-foreground-500">Payment Status</p>
                    <p className="text-lg font-bold text-foreground-900 mt-0.5">
                      {paymentPaid ? (
                        <span className="text-accent-600">Paid</span>
                      ) : 'Pending'}
                    </p>
                  </div>
                  <div className="p-5 rounded-xl border border-background-200/70 bg-background-50">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center mb-3">
                      <i className="ri-file-list-3-line text-lg text-primary-600"></i>
                    </div>
                    <p className="text-xs text-foreground-500">Problem Statements</p>
                    <p className="text-lg font-bold text-foreground-900 mt-0.5">
                      {team?.selected_problem_statement_id ? 'Selected' : 'Available'}
                    </p>
                  </div>
                  <div className="p-5 rounded-xl border border-background-200/70 bg-background-50">
                    <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center mb-3">
                      <i className="ri-calendar-event-line text-lg text-red-600"></i>
                    </div>
                    <p className="text-xs text-foreground-500">Deadline</p>
                    <p className="text-lg font-bold text-foreground-900 mt-0.5">Sept 1, 2026</p>
                  </div>
                </div>

                {/* Quick Actions Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
                  {/* View Problem Statements */}
                  <Link
                    to="/problem-statements"
                    className="p-6 rounded-xl border border-background-200/70 bg-background-50 hover:border-primary-300 transition-all group cursor-pointer"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0 group-hover:bg-primary-200 transition-colors">
                        <i className="ri-file-search-line text-xl text-primary-600"></i>
                      </div>
                      <div>
                        <h3 className="font-heading font-bold text-foreground-950 mb-1">View Problem Statements</h3>
                        <p className="text-xs text-foreground-500">Browse and select your team's problem statement from the published domains</p>
                      </div>
                    </div>
                  </Link>

                  {/* Make Payment */}
                  <Link
                    to="/payment"
                    className={`p-6 rounded-xl border transition-all group cursor-pointer ${
                      paymentPaid
                        ? 'border-accent-200/70 bg-accent-50/50 hover:border-accent-300'
                        : 'border-background-200/70 bg-background-50 hover:border-primary-300'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                        paymentPaid ? 'bg-accent-100 group-hover:bg-accent-200' : 'bg-amber-100 group-hover:bg-amber-200'
                      }`}>
                        <i className={`text-xl ${paymentPaid ? 'ri-check-double-line text-accent-600' : 'ri-bank-card-line text-amber-600'}`}></i>
                      </div>
                      <div>
                        <h3 className="font-heading font-bold text-foreground-950 mb-1">
                          {paymentPaid ? 'Payment Completed' : 'Make Payment'}
                        </h3>
                        <p className="text-xs text-foreground-500">
                          {paymentPaid ? 'Your team registration payment has been confirmed' : 'Complete the registration fee payment (₹2,000) before the deadline'}
                        </p>
                      </div>
                    </div>
                  </Link>

                  {/* Download Brochure */}
                  <a
                    href="#"
                    onClick={(e) => { e.preventDefault(); alert('Brochure download will be available soon!'); }}
                    className="p-6 rounded-xl border border-background-200/70 bg-background-50 hover:border-accent-300 transition-all group cursor-pointer"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center flex-shrink-0 group-hover:bg-accent-200 transition-colors">
                        <i className="ri-download-line text-xl text-accent-600"></i>
                      </div>
                      <div>
                        <h3 className="font-heading font-bold text-foreground-950 mb-1">Download Brochure</h3>
                        <p className="text-xs text-foreground-500">Get the official event brochure with schedule and guidelines</p>
                      </div>
                    </div>
                  </a>

                  {/* View Registration */}
                  <div className="p-6 rounded-xl border border-background-200/70 bg-background-50">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-secondary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-user-settings-line text-xl text-secondary-600"></i>
                      </div>
                      <div>
                        <h3 className="font-heading font-bold text-foreground-950 mb-1">Registration Details</h3>
                        <p className="text-xs text-foreground-500 mb-2">Your team registration information</p>
                        {team && (
                          <div className="space-y-1 text-xs">
                            <p><span className="text-foreground-400">Team ID:</span> <strong className="text-foreground-800">{team.registration_id}</strong></p>
                            <p><span className="text-foreground-400">Team:</span> <strong className="text-foreground-800">{team.team_name}</strong></p>
                            <p><span className="text-foreground-400">College:</span> <span className="text-foreground-700">{team.college_name || '-'}</span></p>
                            <p><span className="text-foreground-400">Status:</span> <span className="text-accent-600 font-medium">Registered</span></p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Announcements */}
                <div className="rounded-2xl border border-background-200/70 bg-background-50 p-6 md:p-8">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-heading text-lg font-bold text-foreground-950">
                      <i className="ri-megaphone-line mr-2 text-foreground-400"></i>
                      Announcements
                    </h2>
                  </div>
                  {announcements.length === 0 ? (
                    <p className="text-sm text-foreground-400 py-8 text-center">No announcements yet</p>
                  ) : (
                    <div className="space-y-3">
                      {announcements.map((a) => (
                        <div key={a.id} className="p-4 rounded-xl bg-background-100/50 border border-background-200/50">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <h3 className="text-sm font-semibold text-foreground-900">{a.title}</h3>
                              <p className="text-xs text-foreground-500 mt-1">{a.content}</p>
                            </div>
                            <span className="text-[11px] text-foreground-400 whitespace-nowrap flex-shrink-0">
                              {new Date(a.published_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </main>
      </>
    </AuthGuard>
  );
}